/**
 * The body figure, front and back: each muscle one or more flat shapes, each
 * shape lit on the half that faces the top left.
 *
 * Adapted from react-body-highlighter's outlines (notice below). Every shape
 * is drawn on the left side and mirrored, so both sides always match; the head
 * is smaller; the neck is split into its two front cords and the trapezius;
 * the front and side of each shoulder have a gap between them; the abs are
 * four blocks a side; the knees have kneecaps; the obliques fill the waist on
 * the back; and the legs of the two views are one length. Kept here rather
 * than taken as a dependency because the map needs continuous shading, a
 * two-colour comparison mode and a distinct treatment for muscles a movement
 * zeroes out (spec §1.4), which the library's own component cannot do.
 *
 * Coordinate space is 0-100 x 0-220 for each view.
 *
 * The licence notice below is a /*! comment so the build keeps it in the files
 * the app and the website ship, as the licence requires.
 */

export type PolygonSlug = string
type Point = readonly [number, number]

export const VIEW_WIDTH = 100
export const VIEW_HEIGHT = 220

const text = (points: readonly Point[]) => points.map(([x, y]) => `${x} ${y}`).join(' ')
const mirror = (points: readonly Point[]): Point[] => points.map(([x, y]): Point => [Math.round((VIEW_WIDTH - x) * 100) / 100, y]).reverse()
/** Each shape as drawn on the left side, then its mirror image on the right. */
const pair = (...left: (readonly Point[])[]) => left.flatMap((shape) => [text(shape), text(mirror(shape))])
/** A shape on the middle line, from its left half: the first and last points lie on the line. */
const middle = (half: readonly Point[]) => text([...half, ...mirror(half).slice(1, -1)])

/*! @license
 * Muscle map outlines adapted from react-body-highlighter
 * (https://github.com/giavinh79/react-body-highlighter), whose polygons in turn
 * derive from react-native-body-highlighter (also MIT).
 *
 * MIT License
 *
 * Copyright (c) 2020 GV79
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 */
export const FRONT_POLYGONS: Readonly<Record<PolygonSlug, readonly string[]>> = {
  head: [middle([[50, 1], [45.6, 1.9], [42.8, 5.6], [42.4, 11.4], [43.4, 16.6], [46, 20.6], [50, 22.8]])],
  neck: pair([[44.4, 24.8], [46.2, 24.6], [49.3, 33.2], [49.3, 38.6]]),
  trapezius: pair([[43.4, 26.6], [47.8, 38.2], [38.4, 39.4], [29.8, 43.4], [30.6, 37.2], [36.4, 35], [41.2, 30.6]]),
  chest: pair([[29.8, 46.5], [31.4, 55.5], [40.8, 58], [48.2, 55.1], [47.8, 42], [37.6, 42]]),
  front_deltoids: pair([[28.6, 37.1], [26.9, 43.3], [28.2, 47.3], [24, 51.2], [23.6, 39.4], [25, 37.1]]),
  side_deltoids: pair([[22, 39.4], [19.6, 41.2], [19.1, 47.8], [20.4, 53.4], [22.4, 51.2]]),
  biceps: pair([[16.7, 68.2], [18, 71.4], [22.9, 66.1], [29, 53.9], [27.8, 49.4], [20.4, 55.9]]),
  triceps: pair([[22.4, 69.4], [29.8, 55.5], [29.8, 60.8], [22.9, 73.1]]),
  forearm: pair(
    [[6.1, 88.6], [10.2, 75.1], [14.7, 70.2], [16.3, 74.3], [19.2, 73.5], [4.5, 97.6], [0.6, 99.6]],
    [[6.9, 101.2], [13.5, 90.6], [18.8, 84.1], [21.6, 77.1], [21.2, 71.8], [4.9, 98.8]],
  ),
  obliques: pair([[33.9, 78.4], [33.1, 71.8], [31, 63.3], [32.2, 57.1], [40.8, 59.2], [39.2, 63.3], [39.2, 83.7]]),
  abs: pair(
    [[43.7, 58.8], [48.6, 57.1], [48.95, 66.2], [41.1, 67.4], [41.2, 64.5]],
    [[41.05, 69], [48.95, 67.8], [48.75, 77.8], [40.8, 79.2]],
    [[40.8, 80.8], [48.72, 79.4], [48.5, 89.6], [40.8, 91.2]],
    [[40.85, 92.8], [48.45, 91.2], [48.2, 107.3], [44.5, 103.7]],
  ),
  abductors: pair([[47.8, 112.59], [44.9, 128.86], [42, 118.46], [40.4, 115.36], [39.6, 108.94], [38, 103.51], [34.7, 94.1], [39.6, 92.22], [41.6, 99.97], [43.7, 106.72]]),
  quadriceps: pair(
    [[34.7, 99.53], [37.1, 109.93], [37.1, 131.63], [34.3, 141.93], [31, 137.05], [29.4, 123], [28.2, 113.48], [29.4, 101.74], [32.2, 94.99]],
    [[38.8, 133.4], [38.4, 114.36], [41.2, 121.22], [44.5, 133.4], [42.9, 139.71], [40, 151.89], [36.3, 152.33], [35.5, 145.14]],
    [[32.7, 143.36], [26.5, 151.45], [25.7, 141.48], [25.7, 131.08], [26.9, 116.69], [29.4, 137.94]],
  ),
  knees: pair([[32.6, 146.69], [35.6, 152.22], [35.4, 159.08], [32.4, 163.51], [28.4, 159.31], [28.4, 153.11]]),
  calves: pair(
    [[26.1, 160.97], [28.2, 167.72], [27.8, 172.7], [25.6, 191.63], [23.6, 206.46], [21.4, 193.84], [20.8, 175.91], [22.4, 169.05], [24.9, 164.62]],
    [[35.5, 165.5], [35.9, 174.91], [35.1, 185.76], [32.2, 191.63], [30.6, 197.5], [26.9, 205.69], [28.2, 189.86], [29, 178.12], [30.2, 165.95]],
  ),
}

export const BACK_POLYGONS: Readonly<Record<PolygonSlug, readonly string[]>> = {
  head: [middle([[50, 1], [45.6, 1.9], [42.8, 5.6], [42.4, 11.4], [44.2, 17.6], [46.8, 20], [50, 20.4]])],
  trapezius: pair([[44.7, 22.6], [47.7, 22.6], [47.2, 38.3], [47.7, 64.7], [38.3, 53.2], [35.3, 40.9], [31.1, 36.6], [39.1, 33.2], [43.8, 27.8]]),
  back_deltoids: pair([[28.6, 37.1], [26.9, 43.3], [28.2, 47.3], [24, 51.2], [23.6, 39.4], [25, 37.1]]),
  side_deltoids: pair([[22, 39.4], [19.6, 41.2], [19.1, 47.8], [20.4, 53.4], [22.4, 51.2]]),
  upper_back: pair([[31.1, 38.7], [28.1, 48.9], [28.5, 55.3], [34, 75.3], [47.2, 71.1], [47.2, 66.4], [36.6, 54], [33.6, 41.3]]),
  triceps: pair(
    [[26.8, 49.8], [17.9, 55.7], [14.5, 72.3], [16.6, 81.7], [21.7, 63.8], [26.8, 55.7]],
    [[26.8, 58.3], [26.8, 68.5], [23, 75.3], [19.1, 77.4], [22.6, 65.5]],
  ),
  lower_back: pair([[47.7, 72.8], [34.5, 77], [35.3, 83.4], [49.4, 102.1]]),
  obliques: pair([[35.6, 86.4], [43.6, 97.2], [31.4, 104.33], [32.6, 93.6]]),
  forearm: pair(
    [[13.6, 75.7], [8.9, 83.8], [6.8, 93.6], [0.6, 105.6], [3.8, 104.3], [12.3, 88.5], [15.7, 83]],
    [[18.7, 79.6], [22.1, 77.9], [20.9, 84.3], [9.4, 103], [6.8, 108.5], [5.1, 104.7]],
  ),
  gluteal: pair([[44.7, 99.6], [30.2, 107.68], [29.8, 116.89], [31.5, 123.48], [47.2, 119.23], [49.4, 113.45]]),
  abductor: pair([[48.1, 120.77], [44.7, 120.77], [41.3, 123.03], [45.1, 140], [48.5, 132.24], [48.9, 126.55]]),
  hamstring: pair(
    [[28.9, 119.96], [31.1, 126.55], [36.6, 123.48], [35.3, 131.88], [34.5, 145.33], [29.4, 152.64], [28.9, 142.26], [27.7, 137.29], [27.2, 128.44]],
    [[38.7, 123.03], [44.3, 141.54], [40.4, 160.32], [36.2, 147.68], [37, 131.88]],
  ),
  knees: pair([[34.5, 148.04], [31.1, 153.37], [33.6, 159.96], [37.4, 156.53]]),
  calves: pair(
    [[29.4, 154.54], [28.5, 160.68], [24.7, 171.88], [23.8, 183.8], [25.5, 187.59], [28.5, 184.16], [29.8, 172.24], [31.9, 164.2], [31.9, 160.32]],
    [[37.4, 158.79], [35.3, 161.13], [33.2, 164.93], [31.1, 172.6], [30.2, 182.99], [34, 190.3], [38.7, 181.81], [39.1, 162.22]],
  ),
  soleus: pair([[28.5, 186.42], [30.2, 186.42], [33.6, 191.84], [30.6, 208.36], [28.5, 202.58], [26.8, 188.76]]),
}

/*
 * Her figure is his, reshaped to a woman's proportions as react-native-body-highlighter's
 * female body has them (measured, not traced): shoulders 15% narrower, ribs and waist
 * narrower, hips up to 10% wider, arms slimmer and closer in, calves slimmer; a bust in
 * place of the pecs, rounder glutes, and a ponytail with a hairline. The hair lies in the
 * space beside the head, so its outline never crosses a muscle.
 */
export const FEMALE_FRONT_POLYGONS: Readonly<Record<PolygonSlug, readonly string[]>> = {
  hair: [
    middle([[50, 0.5], [45.2, 1.4], [42.3, 5.2], [42, 10.2], [43.4, 7.4], [46.2, 5.4], [50, 4.8]]),
    text([[42.3, 5.2], [38.8, 6.6], [36, 11.2], [35.3, 17.6], [36.2, 23.8], [38.2, 28.4], [39.8, 30.4], [40.4, 25.8], [41.3, 19.4], [42.3, 13.6], [42, 10.2]]),
  ],
  head: [middle([[50, 4.8], [46.2, 5.4], [43.4, 7.4], [42, 10.2], [42.4, 12.4], [43.5, 16.4], [46.2, 20.2], [50, 22.4]])],
  neck: pair([[44.8, 24.8], [46.5, 24.6], [49.4, 33.2], [49.4, 38.6]]),
  trapezius: pair([[44.4, 26.6], [48.1, 38.2], [40.1, 39.4], [32.8, 43.4], [33.5, 37.2], [38.4, 35], [42.5, 30.6]]),
  chest: pair([[33.4, 45.2], [33.5, 49.6], [34.8, 53.4], [37.2, 56.1], [40.2, 57.1], [43.4, 56.5], [46.4, 54.6], [48, 51.4], [47.8, 42.8], [39.4, 42.4]]),
  front_deltoids: pair([[31.8, 37.1], [30.4, 43.3], [31.5, 47.3], [27.9, 51.2], [27.6, 39.4], [28.8, 37.1]]),
  side_deltoids: pair([[26.2, 39.4], [24.2, 41.2], [23.7, 47.8], [24.8, 53.4], [26.5, 51.2]]),
  biceps: pair([[20.2, 68.4], [21.2, 71.4], [25.8, 66], [31.8, 53.7], [31, 49.4], [24.2, 56.2]]),
  triceps: pair([[25.9, 69.5], [33, 55.5], [32.7, 60.7], [26.1, 73.1]]),
  forearm: pair(
    [[9.3, 88.6], [13.7, 75.2], [17.9, 70.2], [19.1, 74.1], [21.7, 73.1], [7.3, 97.4], [3.8, 99.6]],
    [[10.1, 101.2], [16.5, 90.5], [21.6, 83.9], [24.4, 76.9], [24.4, 71.8], [8.5, 99]],
  ),
  obliques: pair([[36.6, 78.4], [35.9, 71.8], [34, 63.3], [35, 57.1], [42.3, 59.2], [40.9, 63.3], [40.4, 83.7]]),
  abs: pair(
    [[44.7, 58.8], [48.8, 57.1], [49.1, 66.2], [42.5, 67.4], [42.6, 64.5]],
    [[42.5, 69], [49.1, 67.8], [49, 77.8], [42.2, 79.2]],
    [[42.1, 80.8], [48.9, 79.4], [48.6, 89.6], [41.1, 91.2]],
    [[40.9, 92.8], [48.5, 91.2], [48, 107.3], [44.1, 103.7]],
  ),
  abductors: pair([[47.6, 112.6], [44.7, 128.9], [41.4, 118.5], [39.6, 115.4], [38.6, 108.9], [37.2, 103.5], [34.8, 94.1], [39.8, 92.2], [41.3, 100], [43.1, 106.7]]),
  quadriceps: pair(
    [[34.1, 99.5], [35.9, 109.9], [36.8, 131.6], [34.3, 141.9], [31, 137.1], [28.2, 123], [26.3, 113.5], [28.3, 101.7], [32.2, 95]],
    [[38.7, 133.4], [37.4, 114.4], [40.6, 121.2], [44.4, 133.4], [42.9, 139.7], [40, 151.9], [36.3, 152.3], [35.5, 145.1]],
    [[32.7, 143.4], [26.5, 151.5], [25.7, 141.5], [25.2, 131.1], [25, 116.7], [29.4, 137.9]],
  ),
  knees: pair([[32.8, 146.7], [35.7, 152.2], [35.5, 159.1], [32.7, 163.5], [28.9, 159.3], [28.9, 153.1]]),
  calves: pair(
    [[26.4, 161], [28.3, 167.7], [27.9, 172.7], [25.9, 191.6], [24.1, 206.5], [22, 193.8], [21.5, 175.9], [23, 169.1], [25.3, 164.6]],
    [[35.5, 165.5], [35.8, 174.9], [35.1, 185.8], [32.4, 191.6], [31, 197.5], [27.5, 205.7], [28.7, 189.9], [29.5, 178.1], [30.6, 166]],
  ),
}

export const FEMALE_BACK_POLYGONS: Readonly<Record<PolygonSlug, readonly string[]>> = {
  head: BACK_POLYGONS.head!,
  hair: [text([[57.2, 5.6], [61.2, 6.6], [64, 11.2], [64.7, 17.6], [63.8, 23.8], [61.8, 28.4], [60.2, 30.4], [59.6, 25.8], [58.7, 19.4], [57.8, 14.2], [57.6, 11.4]])],
  trapezius: pair([[45.5, 22.6], [48, 22.6], [47.6, 38.3], [48, 64.7], [40.1, 53.2], [37.5, 40.9], [33.9, 36.6], [40.7, 33.2], [44.7, 27.8]]),
  back_deltoids: pair([[31.8, 37.1], [30.4, 43.3], [31.5, 47.3], [27.9, 51.2], [27.6, 39.4], [28.8, 37.1]]),
  side_deltoids: pair([[26.2, 39.4], [24.2, 41.2], [23.7, 47.8], [24.8, 53.4], [26.5, 51.2]]),
  upper_back: pair([[34.1, 38.7], [31.6, 48.9], [31.9, 55.3], [36.7, 75.3], [47.7, 71.1], [47.7, 66.4], [38.7, 54], [36.2, 41.3]]),
  triceps: pair(
    [[30, 49.8], [22.1, 56], [18.4, 72.5], [19.8, 81.7], [25, 63.8], [29.7, 55.6]],
    [[30, 58.3], [29.5, 68.3], [25.8, 75.1], [22.3, 77.4], [26, 65.6]],
  ),
  lower_back: pair([[48.1, 72.8], [37.1, 77], [36.9, 83.4], [49.4, 102.1]]),
  obliques: pair([[36.8, 86.4], [43.5, 97.2], [30.1, 104.3], [32.7, 93.6]]),
  forearm: pair(
    [[16.8, 75.7], [12.2, 83.9], [9.9, 93.5], [3.8, 105.6], [6.7, 104.2], [15, 88.3], [18.2, 82.7]],
    [[22.2, 79.8], [25.3, 77.9], [23.9, 84.2], [12.6, 103], [10, 108.5], [8.7, 104.9]],
  ),
  gluteal: pair([[45.4, 100.6], [36.6, 103.4], [30.6, 108], [28.2, 113.4], [28.8, 117.4], [31.6, 120.4], [36.4, 121.2], [42.4, 119.6], [47.4, 117.8], [49.4, 113.2]]),
  abductor: pair([[48, 120.8], [44.3, 120.8], [40.8, 123], [45.1, 140], [48.5, 132.2], [48.9, 126.6]]),
  hamstring: pair(
    [[27.4, 120], [30.3, 126.6], [35.9, 123.5], [35, 131.9], [34.5, 145.3], [29.4, 152.6], [28.9, 142.3], [27.7, 137.3], [26.4, 128.4]],
    [[38, 123], [44.3, 141.5], [40.4, 160.3], [36.2, 147.7], [36.8, 131.9]],
  ),
  knees: pair([[34.7, 148], [31.5, 153.4], [33.9, 160], [37.5, 156.5]]),
  calves: pair(
    [[29.6, 154.5], [28.8, 160.7], [25.3, 171.9], [24.5, 183.8], [26, 187.6], [28.8, 184.2], [30, 172.2], [31.9, 164.2], [31.9, 160.3]],
    [[37.4, 158.8], [35.5, 161.1], [33.6, 164.9], [31.6, 172.6], [30.8, 183], [34.3, 190.3], [38.6, 181.8], [39, 162.2]],
  ),
  soleus: pair([[28.9, 186.4], [30.5, 186.4], [33.6, 191.8], [30.8, 208.4], [28.9, 202.6], [27.3, 188.8]]),
}

/** Whose body a figure is: hers when the lifter said female, his otherwise. */
export type Figure = 'male' | 'female'

export const FIGURES: Readonly<Record<Figure, { front: Readonly<Record<PolygonSlug, readonly string[]>>; back: Readonly<Record<PolygonSlug, readonly string[]>> }>> = {
  male: { front: FRONT_POLYGONS, back: BACK_POLYGONS },
  female: { front: FEMALE_FRONT_POLYGONS, back: FEMALE_BACK_POLYGONS },
}

/** How far a shape's lit part is mixed toward white, and the rest toward black, in percent. */
export const LIT = 12
export const SHADED = 10

/** A shape's two tones for its colour, any CSS colour: the lit part's and the rest's. */
export function tones(colour: string): readonly [lit: string, shaded: string] {
  return [`color-mix(in srgb, ${colour} ${100 - LIT}%, white)`, `color-mix(in srgb, ${colour} ${100 - SHADED}%, black)`]
}

function corners(points: string): Point[] {
  const n = points.trim().split(/\s+/).map(Number)
  return Array.from({ length: n.length >> 1 }, (_, i): Point => [n[2 * i]!, n[2 * i + 1]!])
}

/** Whether segment a-b crosses segment c-d; meeting at an end does not count. */
function crosses(a: Point, b: Point, c: Point, d: Point): boolean {
  const turn = (o: Point, p: Point, q: Point) => (p[0] - o[0]) * (q[1] - o[1]) - (p[1] - o[1]) * (q[0] - o[0])
  return turn(c, d, a) * turn(c, d, b) < 0 && turn(a, b, c) * turn(a, b, d) < 0
}

function contains(shape: readonly Point[], [x, y]: Point): boolean {
  let inside = false
  for (let i = 0, j = shape.length - 1; i < shape.length; j = i++) {
    const [xi, yi] = shape[i]!
    const [xj, yj] = shape[j]!
    if (yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi) inside = !inside
  }
  return inside
}

const litParts = new Map<string, string | undefined>()

/**
 * The part of a shape that catches the light, as points, or undefined when it
 * catches none. The shape folds along its longest line from corner to corner
 * that stays inside it, and the half facing the top left is lit, as on the rank
 * emblems. When that line is one of its edges, the whole shape is lit or none of
 * it. Draw the shape in its shaded tone, then this over it in its lit tone.
 */
export function litPart(points: string): string | undefined {
  if (litParts.has(points)) return litParts.get(points)
  const shape = corners(points)
  const n = shape.length
  let from = 0
  let to = 1
  let longest = -1
  for (let i = 0; i < n; i++) {
    for (let j = i + 1; j < n; j++) {
      const a = shape[i]!
      const b = shape[j]!
      const length = Math.hypot(b[0] - a[0], b[1] - a[1])
      if (length <= longest) continue
      const edge = j === i + 1 || (i === 0 && j === n - 1)
      if (!edge && (!contains(shape, [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2]) || shape.some((c, k) => crosses(a, b, c, shape[(k + 1) % n]!)))) continue
      longest = length
      from = i
      to = j
    }
  }
  const [ax, ay] = shape[from]!
  const [bx, by] = shape[to]!
  // How far a half's corners lie from the fold toward the light, on average.
  const light = (part: readonly Point[]) => (part.reduce((sum, [x, y]) => sum + (x - ax) * (ay - by) + (y - ay) * (bx - ax), 0) / part.length) * (by - ay - bx + ax)
  const one = shape.slice(from, to + 1)
  const two = [...shape.slice(to), ...shape.slice(0, from + 1)]
  const lit = light(one) >= light(two) ? one : two
  const out = lit.length >= 3 && light(lit) > 0 ? text(lit) : undefined
  litParts.set(points, out)
  return out
}
